import { redirect } from '@tanstack/react-router';

import type { ActiveDashboard } from '@/utils/session/dashboard-from-session';
import type { WorkspaceOptions } from '@/utils/session/workspace-options';

import { BASE_PATH } from '@/constants/config';
import { getSafeReturnTo } from '@/utils/get-safe-return-to';
import { fetchResearcherMemberships } from '@/utils/session/fetch-researcher-memberships';
import { persistActiveDashboard } from '@/utils/session/persist-active-dashboard';
import { readRememberedDashboard } from '@/utils/session/read-remembered-dashboard';
import {
  decideWorkspaceOptions,
  selectableTeams,
} from '@/utils/session/workspace-options';

interface ResearcherUser {
  id: string;
  teams?: { teamId: string; organizationId: string }[];
}

interface ResolveActiveDashboardArgs<TUser extends ResearcherUser> {
  context: {
    user: TUser;
    activeDashboard: ActiveDashboard;
    v3Enabled?: boolean;
  };
  location: {
    pathname: string;
    searchStr: string;
  };
}

interface ResolvedActiveDashboard<TUser> {
  user: TUser;
  activeDashboard: ActiveDashboard;
  v3Enabled?: boolean;
}

/**
 * Resolve the researcher's active organization + team for an authenticated
 * route's `beforeLoad`.
 *
 *   1. Session already has both — cheap path, returned as-is without asking
 *      core-api anything.
 *   2. Otherwise: pick one workspace, write it, and apply what was written.
 *      A workspace is picked without asking the user only when there is no
 *      real choice — the one they last used (remembered in the
 *      `last_dashboard` cookie), or the only one they belong to. Writing it
 *      into the session means the next navigation takes case 1, and the
 *      legacy site sees the same active dashboard.
 *   3. Anything else — a real choice, a pick that the write rejected, or a
 *      memberships lookup that failed — goes to `/researchers/select-team`
 *      with a `returnTo` pointing back at the current location.
 *
 * Which of those applies is decided by `decideWorkspaceOptions`, shared with
 * the picker so the two cannot disagree about whether a workspace is
 * resolvable.
 *
 * Only a researcher who genuinely belongs to nothing goes to
 * `/researchers/onboarding`. A memberships lookup that *failed* must not:
 * onboarding bounces anyone whose session already lists organizations right
 * back here, and back it comes with the same failed lookup — an onboarding ↔
 * projects redirect loop on a transient outage.
 *
 * Nothing is applied that was not successfully written. `persistActiveDashboard`
 * re-checks membership against `session.user.teams` and is the last word on
 * it; binding a rejected pair would leave the route addressing a team the
 * researcher is not on, with every request under it failing.
 *
 * This is the shared resolution used by every researcher area (projects,
 * participants, …). It deliberately does NOT perform the `v3Enabled` →
 * legacy-dashboard redirect: that target differs per area, so each route
 * inspects the returned `v3Enabled` and redirects itself.
 */

/** Send the picker back where the user was actually headed, if it is safe to. */
function returnToSearch(location: { pathname: string; searchStr: string }) {
  const candidate = `${BASE_PATH}${location.pathname}${location.searchStr}`;
  const safeReturnTo = getSafeReturnTo(candidate);
  return safeReturnTo ? { search: { returnTo: safeReturnTo } } : {};
}

/**
 * The one workspace we may enter without asking, or `null` when the
 * researcher has a real choice to make.
 *
 * Candidates come from `selectableTeams`, which is already narrowed to the
 * researcher's own memberships — not the org team list, which is every team
 * in the org (core-api's `findAllForOrganization`) including ones they were
 * removed from. Picking off that list produced a doomed write on every login
 * for a revoked membership.
 *
 * Preference order: the workspace they last used, then their only one.
 */
async function pickDashboard(
  workspaces: Extract<WorkspaceOptions, { status: 'choose' }>,
  userId: string,
): Promise<{ organizationId: string; teamId: string } | null> {
  const ownTeams = selectableTeams(workspaces);

  const remembered = await readRememberedDashboard({
    data: { userId },
  }).catch((error: unknown) => {
    // Remembering is a convenience; failing to read it must never be the
    // reason a route load fails. Fall through to the single-team case.
    console.error('Failed to read remembered dashboard', error);
    return null;
  });

  if (
    remembered &&
    ownTeams.some(
      (team) =>
        team.organizationId === remembered.organizationId &&
        team.teamId === remembered.teamId,
    )
  ) {
    return remembered;
  }

  return ownTeams.length === 1 ? (ownTeams[0] ?? null) : null;
}

export async function resolveActiveDashboard<TUser extends ResearcherUser>({
  context,
  location,
}: ResolveActiveDashboardArgs<TUser>): Promise<ResolvedActiveDashboard<TUser>> {
  const { user, activeDashboard } = context;

  if (activeDashboard.team) {
    return { user, activeDashboard, v3Enabled: context.v3Enabled };
  }

  const result = await fetchResearcherMemberships({
    data: { userId: user.id },
  });

  const workspaces = decideWorkspaceOptions(result, user.teams ?? []);

  if (workspaces.status === 'no-organizations') {
    throw redirect({ to: '/researchers/onboarding' });
  }

  // `lookup-failed` and `no-teams` both need the picker: one to offer a retry,
  // the other to explain there is nothing to enter. Neither is onboarding.
  if (workspaces.status !== 'choose') {
    throw redirect({
      to: '/researchers/select-team',
      ...returnToSearch(location),
    });
  }

  const candidate = await pickDashboard(workspaces, user.id);
  if (!candidate) {
    throw redirect({
      to: '/researchers/select-team',
      ...returnToSearch(location),
    });
  }

  const written = await persistActiveDashboard({ data: candidate }).catch(
    (error: unknown) => {
      // A write failure must not abort the route load.
      console.error('Failed to persist active dashboard', error);
      return null;
    },
  );

  if (!written?.success) {
    throw redirect({
      to: '/researchers/select-team',
      ...returnToSearch(location),
    });
  }

  return {
    user,
    activeDashboard: {
      organization: candidate.organizationId,
      team: candidate.teamId,
    },
    v3Enabled: written.v3Enabled,
  };
}
